import React, { useState, useEffect } from 'react';
import { Referral } from '../services/apiService';
import { AuthService } from '../services/authService';
import { Modal } from './Modal';
import { Spinner } from './Spinner';
import FeatherIcon from 'feather-icons-react';

interface FollowUpComposerProps {
  referral: Referral;
  isOpen: boolean;
  onClose: () => void;
}

interface Template {
  id: string;
  name: string;
}

interface DraftData {
  subject: string;
  body: string;
  template_used: string;
  template_name: string;
  missing_fields: any[];
  generated_at: string;
  referrer_email?: string;
}

export const FollowUpComposer: React.FC<FollowUpComposerProps> = ({ 
  referral, 
  isOpen, 
  onClose 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftData | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState<string>('');
  const [editedBody, setEditedBody] = useState<string>('');
  const [copiedFeedback, setCopiedFeedback] = useState<string | null>(null);
  const [emailCopied, setEmailCopied] = useState(false);

  // Load suggested template and missing fields when modal opens
  useEffect(() => {
    if (isOpen && referral) {
      loadTemplateData();
    }
  }, [isOpen, referral]);

  const loadTemplateData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await AuthService.getAccessToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';
      
      // Get suggested template and available templates
      const response = await fetch(
        `${baseUrl}/referrals/${referral.id}/suggested-template`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load templates: ${response.statusText}`);
      }

      const data = await response.json();
      setTemplates(data.all_templates || []);
      setSelectedTemplate(data.template_id || '');

      // Generate draft with suggested template
      generateDraft(data.template_id || '');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load templates';
      setError(errorMsg);
      console.error('Error loading template data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateDraft = async (templateId: string) => {
    setLoading(true);
    setError(null);
    try {
      const token = await AuthService.getAccessToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const baseUrl = (import.meta.env.VITE_FUNCTION_APP_URL || 'https://func-axis-referral-dev.azurewebsites.net') + '/api';

      const response = await fetch(
        `${baseUrl}/referrals/${referral.id}/generate-draft`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ template_id: templateId })
        }
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || `Failed to generate draft: ${response.statusText}`);
      }

      const data = await response.json();
      setDraft(data);
      setEditedSubject(data.subject);
      setEditedBody(data.body);
      setIsEditing(false);
      setCopiedFeedback(null);
      setEmailCopied(false);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate draft';
      setError(errorMsg);
      console.error('Error generating draft:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    generateDraft(templateId);
  };

  const handleCopyToClipboard = async () => {
    try {
      const textToCopy = `Subject: ${editedSubject}\n\n${editedBody}`;
      
      // Try using Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
        setCopiedFeedback('✓ Copied to clipboard!');
        setTimeout(() => setCopiedFeedback(null), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedFeedback('✓ Copied to clipboard!');
        setTimeout(() => setCopiedFeedback(null), 2000);
      }
    } catch (err) {
      setError('Unable to copy. Please select and copy manually.');
      console.error('Copy failed:', err);
    }
  };

  const handleCopyEmail = async () => {
    if (!draft?.referrer_email) return;
    
    try {
      const emailToCopy = draft.referrer_email;
      
      // Try using Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(emailToCopy);
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = emailToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 2000);
      }
    } catch (err) {
      setError('Unable to copy email. Please select and copy manually.');
      console.error('Email copy failed:', err);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Switching back to preview mode - revert changes
      if (draft) {
        setEditedSubject(draft.subject);
        setEditedBody(draft.body);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleResetToTemplate = () => {
    if (draft) {
      setEditedSubject(draft.subject);
      setEditedBody(draft.body);
      setIsEditing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="followup-composer-modal"
      preventCloseOnOverlayClick={loading}
    >
      <div className="modal-header">
        <h3>Generate Follow-up Email</h3>
        <button className="btn-close" onClick={onClose} aria-label="Close modal">
          ×
        </button>
      </div>

      <div className="modal-body">
        {/* Error Message */}
        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
            <button 
              className="btn btn-sm btn-link"
              onClick={() => loadTemplateData()}
              >
                Retry
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <Spinner size="lg" color="default" />
              <p>Generating draft...</p>
            </div>
          )}

          {/* Main Content - Two Column Layout */}
          {!loading && draft && (
            <div className="composer-content-wrapper">
              {/* Left Column - Details and Template Selection */}
              <div className="composer-left-panel">
                {/* Referral Info */}
                <div className="referral-info-section">
                  <p><strong>Patient:</strong> {referral.patient_first_name} {referral.patient_last_name}</p>
                </div>

                {/* Referrer Email */}
                {draft.referrer_email && (
                  <div className="referrer-email-section">
                    <label>Referrer Email:</label>
                    <div className="referrer-email-display">
                      <span>{draft.referrer_email}</span>
                      <button
                        onClick={handleCopyEmail}
                        className={`referrer-email-copy-btn ${emailCopied ? 'copied' : ''}`}
                        title={emailCopied ? 'Email copied!' : 'Copy email address'}
                        aria-label="Copy referrer email address"
                      >
                        <FeatherIcon 
                          icon={emailCopied ? "check" : "copy"} 
                          size={18}
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* Template Selection */}
                <div className="template-selection-group">
                  <label htmlFor="template-select">
                    Select Template
                    <span className="confidence-badge">{draft.template_used === selectedTemplate ? '(Recommended)' : ''}</span>
                  </label>
                  <select
                    id="template-select"
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="form-control"
                    disabled={loading}
                  >
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Missing Fields Display */}
                {draft.missing_fields && draft.missing_fields.length > 0 && (
                  <div className="missing-fields-section">
                    <h4>Missing Information Detected:</h4>
                    <ul className="missing-fields-list">
                      {draft.missing_fields.map((field, idx) => (
                        <li key={idx} className={`field-item ${field.category}`}>
                          <strong>{field.display_label}</strong>
                          {field.category === 'invalid_identifier' && (
                            <span className="reason"> - {field.reason_message}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Right Column - Email Draft */}
              <div className="draft-preview-section">
                <div className="draft-header">
                  <h4>Email Draft</h4>
                  <div className="draft-controls">
                    <button
                      className={`btn btn-sm ${isEditing ? 'btn-secondary' : 'btn-outline'}`}
                      onClick={handleEditToggle}
                      title={isEditing ? 'Exit edit mode' : 'Edit draft text'}
                    >
                      {isEditing ? '✓ Done Editing' : '✎ Edit'}
                    </button>
                    {isEditing && (
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={handleResetToTemplate}
                        title="Revert to original template"
                      >
                        ⟲ Reset
                      </button>
                    )}
                  </div>
                </div>

                {/* Subject Line */}
                <div className="draft-field">
                  <label>Subject:</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      className="form-control subject-input"
                      placeholder="Email subject"
                    />
                  ) : (
                    <div className="draft-subject">{editedSubject}</div>
                  )}
                </div>

                {/* Body Text */}
                <div className="draft-field">
                  <label>Body:</label>
                  {isEditing ? (
                    <textarea
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      className="form-control body-textarea"
                      placeholder="Email body"
                      rows={12}
                    />
                  ) : (
                    <div className="draft-body">{editedBody}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Copy Feedback */}
          {copiedFeedback && (
            <div className="alert alert-success">
              {copiedFeedback}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCopyToClipboard}
            disabled={loading || !draft}
            title="Copy draft to clipboard for pasting into email client"
          >
            📋 Copy to Clipboard
          </button>
        </div>
      </Modal>
  );
};



